import { components } from "react-select";
import { twMerge } from "tailwind-merge";

import type { IDropdownOption } from "@repo/design-system";
import type React from "react";
import type { SingleValueProps, InputProps } from "react-select";

interface IDropdownsUnificationProps<T> {
  isSearchable?: boolean;
  getSelectedValue?: (option: T) => string;
}

export function useDropdownsUnification<
  T extends IDropdownOption = IDropdownOption,
>(props?: IDropdownsUnificationProps<T>) {
  const CustomSingleValue = <T extends IDropdownOption>(
    props: SingleValueProps<T>,
  ) => {
    const { children, data } = props;

    return (
      <components.SingleValue {...props}>
        <div className="flex items-center w-full">
          {data.icon && (
            <span className="mr-2 flex items-center">{data.icon}</span>
          )}
          <span className="truncate text-contentPrimary">{children}</span>
        </div>
      </components.SingleValue>
    );
  };

  if (props?.isSearchable === false) {
    return {
      SingleValue: CustomSingleValue,
    };
  }

  const getSelectedValueProxy = (normalizedOption: T) =>
    props?.getSelectedValue?.(normalizedOption) ?? normalizedOption.label;

  const InputComponent: React.FC<InputProps<T>> = (props) => {
    const isExpended = props.selectProps.menuIsOpen;
    const isValueSelected = !!props.selectProps.value;

    const placeholder = isValueSelected
      ? getSelectedValueProxy(props.selectProps.value as T)
      : props.selectProps.placeholder?.toString();

    return (
      <input
        ref={(input) => props.innerRef?.(input)}
        value={props.value}
        onBlur={props.onBlur}
        autoFocus={isExpended}
        onFocus={props.onFocus}
        onChange={props.onChange}
        placeholder={placeholder}
        className={twMerge(
          "outline-hidden flex-1 bg-transparent",
          isValueSelected
            ? "placeholder:text-contentPrimary"
            : "placeholder:text-contentQuartenary",
        )}
      />
    );
  };

  return {
    components: {
      Input: InputComponent,
      Placeholder: () => null,
      SingleValue: () => null,
    },
  };
}
